import React, { useEffect, useMemo, useState, useRef } from 'react';
import {connect, useSelector} from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    Plus, X, Trash2, Edit, HelpCircle, Search, Check,
    Filter, List, Info, Database
} from "lucide-react";
import {
    getAllQuizzesRequest,
    getQuizzesByModuleRequest,
    getQuizzesByTypeRequest,
    createQuizRequest,
    updateQuizRequest,
    deleteQuizRequest,
    clearQuizMessage
} from '../../redux/actions/quizActions';
import './QuizManagement.css';
import { getAllModulesRequest } from "../../redux/actions/moduleActions";
import { fetchDomainesRequest } from "../../redux/actions/paramDomainesThemesActions";
import { fetchSkillsByQuizModeRequest } from "../../redux/actions/skillsActions";

const QUIZ_TYPE_OPTIONS = [
    { value: 'STANDARD', label: 'Standard' },
    { value: 'EVALUATION', label: 'Évaluation' },
    { value: 'INTEGRATION', label: 'Intégration' },
    { value: 'MUST_LEARN', label: 'Must Learn' }
];

const DECLENCHEMENT_MODE_OPTIONS = [
    { value: 'IMMEDIAT', label: 'Immédiat' },
    { value: 'DIFFERE', label: 'Différé' }
];

const emptySkill = { skillId: '' };

const MODULE_CONTEXT_LABELS = {
    FORMATION: 'Formation',
    ONBOARDING: 'Onboarding',
    SKILLS: 'Competences'
};

const MODULE_CONTEXT_QUESTION_PATHS = {
    FORMATION: '/formation/quizzes',
    ONBOARDING: '/recrutement/onboarding/quizzes',
    SKILLS: '/skills/quizzes'
};

const normalizeText = (value) =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toUpperCase();

const getModuleLabel = (module) => module?.name || module?.nom || module?.libelle || '';
const getModuleCode = (module) => module?.code || module?.moduleCode || '';

const moduleMatchesContext = (module, moduleContext) => {
    const context = normalizeText(moduleContext);
    if (!context) return true;

    const code = normalizeText(getModuleCode(module));
    const label = normalizeText(getModuleLabel(module));
    const route = normalizeText(module?.routePath || module?.modulePath || module?.module_path);

    if (context === 'FORMATION') {
        return code.includes('FORMATION') || label.includes('FORMATION') || route.includes('FORMATION');
    }
    if (context === 'ONBOARDING') {
        return code.includes('ONBOARDING') || label.includes('ONBOARDING') || route.includes('ONBOARDING')
            || code.includes('RECRUTEMENT') || label.includes('RECRUTEMENT') || route.includes('RECRUTEMENT');
    }
    if (context === 'SKILLS') {
        return code.includes('SKILLS') || label.includes('COMPETENCE') || label.includes('SKILLS') || route.includes('SKILLS');
    }

    return true;
};

const findContextModule = (modules = [], moduleContext) => {
    const matches = (modules || []).filter((module) => moduleMatchesContext(module, moduleContext));
    if (normalizeText(moduleContext) === 'ONBOARDING') {
        return matches.find((module) => {
            const text = normalizeText(`${getModuleCode(module)} ${getModuleLabel(module)} ${module?.routePath || ''}`);
            return text.includes('ONBOARDING');
        }) || matches[0];
    }
    return matches[0];
};

const inferModuleContextFromRole = (role) => {
    const text = normalizeText(`${role?.code || ''} ${role?.name || ''} ${role?.moduleName || ''} ${role?.routePath || ''}`);
    if (text.includes('ONBOARDING')) return 'ONBOARDING';
    if (text.includes('SKILLS') || text.includes('COMPETENCE')) return 'SKILLS';
    if (text.includes('FORMATION')) return 'FORMATION';
    return '';
};

const QuizModal = ({ isOpen, onClose, onSubmit, editingQuiz, loading, modules, skills, domaines, fetchSkillsByQuizMode, lockedModuleId }) => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        titre: '',
        description: '',
        moduleId: '',
        typeQuiz: 'STANDARD',
        declenchementMode: 'IMMEDIAT',
        delaiDeclenchementH: '',
        seuilReussite: 70,
        nbMaxTentatives: 1,
        estObligatoire: false,
        dureeMin: '',
        refContexteId: '',
        competences: []
    });

    const [skillSearch, setSkillSearch] = useState("");
    const [showSkillDropdown, setShowSkillDropdown] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowSkillDropdown(false);
            }
        };
        if (showSkillDropdown) {
            window.addEventListener("mousedown", handleClickOutside, true);
        }
        return () => window.removeEventListener("mousedown", handleClickOutside, true);
    }, [showSkillDropdown]);

    useEffect(() => {
        if (editingQuiz) {
            setFormData({
                titre: editingQuiz.titre || '',
                description: editingQuiz.description || '',
                moduleId: editingQuiz.moduleId || lockedModuleId || '',
                typeQuiz: editingQuiz.typeQuiz || 'STANDARD',
                declenchementMode: editingQuiz.declenchementMode || 'IMMEDIAT',
                delaiDeclenchementH: editingQuiz.delaiDeclenchementH ?? '',
                seuilReussite: editingQuiz.seuilReussite ?? 70,
                nbMaxTentatives: editingQuiz.nbMaxTentatives ?? 1,
                estObligatoire: !!editingQuiz.estObligatoire,
                dureeMin: editingQuiz.dureeMin ?? '',
                refContexteId: editingQuiz.refContexteId ?? '',
                competences: editingQuiz.competences?.map((c) => ({
                    skillId: c.skillId
                })) || []
            });
        } else {
            setFormData({
                titre: '',
                description: '',
                moduleId: lockedModuleId || '',
                typeQuiz: 'STANDARD',
                declenchementMode: 'IMMEDIAT',
                delaiDeclenchementH: '',
                seuilReussite: 70,
                nbMaxTentatives: 1,
                estObligatoire: false,
                dureeMin: '',
                refContexteId: '',
                competences: []
            });
        }
        setSkillSearch("");
        setShowSkillDropdown(false);
    }, [editingQuiz, isOpen, lockedModuleId]);

    useEffect(() => {
        if (isOpen) {
            fetchSkillsByQuizMode();
        }
    }, [isOpen, fetchSkillsByQuizMode]);

    if (!isOpen) return null;

    const updateField = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const toggleSkill = (skillId) => {
        setFormData((prev) => {
            const exists = prev.competences.some(c => c.skillId === skillId);
            if (exists) {
                return {
                    ...prev,
                    competences: prev.competences.filter(c => c.skillId !== skillId)
                };
            } else {
                return {
                    ...prev,
                    competences: [...prev.competences, { skillId }]
                };
            }
        });
    };


    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            ...formData,
            moduleId: formData.moduleId ? Number(formData.moduleId) : null,
            delaiDeclenchementH: formData.delaiDeclenchementH !== '' ? Number(formData.delaiDeclenchementH) : null,
            seuilReussite: Number(formData.seuilReussite),
            nbMaxTentatives: Number(formData.nbMaxTentatives),
            dureeMin: formData.dureeMin !== '' ? Number(formData.dureeMin) : null,
            refContexteId: formData.refContexteId !== '' ? Number(formData.refContexteId) : null,
            competences: formData.competences
                .filter((c) => c.skillId)
                .map((c) => ({
                    skillId: Number(c.skillId)
                }))
        };

        onSubmit(payload);
    };

    return (
        <div className="qz-modal-overlay" onClick={onClose}>
            <div className="qz-modal" onClick={(e) => e.stopPropagation()}>
                <div className="qz-modal-header">
                    <h3>
                        <HelpCircle size={16} style={{ color: "#ee645f" }} />
                        {editingQuiz ? t('Modifier le quiz') : t('Ajouter un quiz')}
                    </h3>
                    <button className="qz-x" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="qz-modal-body">
                    <form id="quiz-form" onSubmit={handleSubmit}>
                        <div className="qz-grid">
                            <div className="qz-field qz-field--full">
                                <label>{t('Titre')} *</label>
                                <input
                                    value={formData.titre}
                                    onChange={(e) => updateField('titre', e.target.value)}
                                    placeholder={t('Nom du quiz')}
                                    required
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Module')} *</label>
                                <select
                                    value={formData.moduleId}
                                    onChange={(e) => updateField('moduleId', e.target.value)}
                                    required
                                    disabled={!!lockedModuleId}
                                >
                                    <option value="">{t('Sélectionner...')}</option>
                                    {modules.map((module) => (
                                        <option key={module.id} value={module.id}>{getModuleLabel(module)}</option>
                                    ))}
                                </select>
                            </div>


                            <div className="qz-field">
                                <label>{t('Type de quiz')} *</label>
                                <select
                                    value={formData.typeQuiz}
                                    onChange={(e) => updateField('typeQuiz', e.target.value)}
                                    required
                                >
                                    {QUIZ_TYPE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="qz-field qz-field--full">
                                <label>{t('Description')}</label>
                                <textarea
                                    rows="3"
                                    value={formData.description}
                                    onChange={(e) => updateField('description', e.target.value)}
                                    placeholder={t('Description courte...')}
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Mode de déclenchement')}</label>
                                <select
                                    value={formData.declenchementMode}
                                    onChange={(e) => updateField('declenchementMode', e.target.value)}
                                >
                                    {DECLENCHEMENT_MODE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="qz-field">
                                <label>{t('Seuil de réussite (%)')} *</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={formData.seuilReussite}
                                    onChange={(e) => updateField('seuilReussite', e.target.value)}
                                    required
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Nb max tentatives')} *</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={formData.nbMaxTentatives}
                                    onChange={(e) => updateField('nbMaxTentatives', e.target.value)}
                                    required
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Durée (min)')}</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={formData.dureeMin}
                                    onChange={(e) => updateField('dureeMin', e.target.value)}
                                />
                            </div>

                            {formData.typeQuiz === 'MUST_LEARN' && formData.declenchementMode === 'DIFFERE' && (
                                <div className="qz-field">
                                    <label>{t('Délai de déclenchement (heures)')} *</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.delaiDeclenchementH}
                                        onChange={(e) => updateField('delaiDeclenchementH', e.target.value)}
                                        required
                                    />
                                </div>
                            )}

                            <div className="qz-field">
                                <label>{t('Ref contexte ID')}</label>
                                <input
                                    type="number"
                                    value={formData.refContexteId}
                                    onChange={(e) => updateField('refContexteId', e.target.value)}
                                    placeholder="Ex: 101"
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Quiz obligatoire')}</label>
                                <button
                                    type="button"
                                    className={`qz-radio-toggle ${formData.estObligatoire ? 'is-checked' : ''}`}
                                    onClick={() => updateField('estObligatoire', !formData.estObligatoire)}
                                    aria-pressed={formData.estObligatoire}
                                    aria-label={t('Quiz obligatoire')}
                                >
                                    <span className="qz-radio-dot" />
                                    <span>{t('Quiz obligatoire')}</span>
                                </button>
                            </div>
                        </div>

                        <div className="qz-divider" />

                        {(() => {
                            const selectedModule = modules.find(m => String(m.id) === String(formData.moduleId));
                            const selectedModuleText = normalizeText(`${getModuleLabel(selectedModule)} ${getModuleCode(selectedModule)}`);
                            return selectedModuleText.includes('COMPETENCE') || selectedModuleText.includes('SKILLS');
                        })() && (
                            <div className="qz-section" ref={dropdownRef}>
                                <div className="qz-section-head">
                                    <h4>{t('Compétences liées')}</h4>
                                    <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>
                                        {formData.competences.length} {t('sélectionnée(s)')}
                                    </span>
                                </div>

                                <div style={{ position: 'relative' }}>
                                    <div 
                                        className="qz-multi-select"
                                        onClick={() => setShowSkillDropdown(true)}
                                    >
                                        {formData.competences.map((c) => {
                                            const s = skills.find(sk => sk.id === c.skillId);
                                            return (
                                                <span key={c.skillId} className="qz-badge qz-badge--blue" style={{ gap: 6, padding: '4px 8px' }}>
                                                    {s?.nom || s?.name || s?.code}
                                                    <X 
                                                        size={12} 
                                                        style={{ cursor: 'pointer' }} 
                                                        onClick={(e) => { e.stopPropagation(); toggleSkill(c.skillId); }} 
                                                    />
                                                </span>
                                            );
                                        })}
                                        <input 
                                            type="text" 
                                            placeholder={formData.competences.length === 0 ? t('Rechercher une compétence...') : ""}
                                            value={skillSearch}
                                            onChange={(e) => { setSkillSearch(e.target.value); setShowSkillDropdown(true); }}
                                            onFocus={() => setShowSkillDropdown(true)}
                                            className="qz-multi-select-input"
                                        />
                                        { (skillSearch || showSkillDropdown) && (
                                            <X 
                                                size={14} 
                                                style={{ cursor: 'pointer', color: '#9ca3af' }} 
                                                onClick={(e) => { e.stopPropagation(); setSkillSearch(""); setShowSkillDropdown(false); }} 
                                            />
                                        )}
                                    </div>

                                    {showSkillDropdown && (
                                        <div className="qz-dropdown-menu">
                                            {skills
                                                .filter(s => 
                                                    !skillSearch || 
                                                    (s.nom || '').toLowerCase().includes(skillSearch.toLowerCase()) || 
                                                    (s.code || '').toLowerCase().includes(skillSearch.toLowerCase())
                                                )
                                                .map((s) => {
                                                    const isSelected = formData.competences.some(c => c.skillId === s.id);
                                                    return (
                                                        <div 
                                                            key={s.id}
                                                            className={`qz-dropdown-item ${isSelected ? 'active' : ''}`}
                                                            onClick={() => toggleSkill(s.id)}
                                                        >
                                                            <span>
                                                                <span style={{ fontWeight: 600 }}>{s.nom}</span>
                                                                <span style={{ fontSize: 10, marginLeft: 6, opacity: 0.6 }}>({s.code})</span>
                                                            </span>
                                                            {isSelected && <Check size={14} style={{ color: '#ee645f' }} />}
                                                        </div>
                                                    );
                                                })
                                            }
                                            {skills.filter(s => !skillSearch || (s.nom || '').toLowerCase().includes(skillSearch.toLowerCase()) || (s.code || '').toLowerCase().includes(skillSearch.toLowerCase())).length === 0 && (
                                                <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: 12 }}>
                                                    {t('Aucune compétence trouvée')}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                </div>

                <div className="qz-modal-footer">
                    <button className="qz-btn qz-btn-light" onClick={onClose} disabled={loading}>
                        {t('Annuler')}
                    </button>
                    <button className="qz-btn qz-btn-primary" type="submit" form="quiz-form" disabled={loading}>
                        {loading ? t('Chargement...') : (editingQuiz ? t('Mettre à jour') : t('Créer'))}
                    </button>
                </div>
            </div>
        </div>
    );
};

const QuizManagement = ({
    moduleContext,
    quizzes,
    loading,
    message,
    error,
    modules = [],
    skills = [],
    domaines = [],
    getAllQuizzes,
    getAllModules,
    getAllDomaines,
    getQuizzesByModule,
    getQuizzesByType,
    createQuiz,
    updateQuiz,
    deleteQuiz,
    fetchSkillsByQuizMode,
    clearMessage
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingQuiz, setEditingQuiz] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedModuleId, setSelectedModuleId] = useState('');
    const selectedRole = useSelector(state => state.role?.selectedRole);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const effectiveModuleContext = moduleContext || inferModuleContextFromRole(selectedRole);
    const contextModules = useMemo(
        () => (effectiveModuleContext ? (modules || []).filter((module) => moduleMatchesContext(module, effectiveModuleContext)) : (modules || [])),
        [modules, effectiveModuleContext]
    );
    const contextModule = useMemo(
        () => findContextModule(modules, effectiveModuleContext),
        [modules, effectiveModuleContext]
    );
    const lockedModuleId = effectiveModuleContext ? contextModule?.id : '';
    const questionsBasePath = MODULE_CONTEXT_QUESTION_PATHS[effectiveModuleContext] || '/quizzes';

    useEffect(() => {
        getAllModules();
        getAllDomaines();
        return () => clearMessage();
    }, [getAllModules, getAllDomaines, clearMessage]);

    useEffect(() => {
        setCurrentPage(1);
        if (effectiveModuleContext) {
            if (lockedModuleId) {
                setSelectedModuleId(String(lockedModuleId));
                getQuizzesByModule(lockedModuleId);
            }
            return;
        }
        getAllQuizzes();
    }, [effectiveModuleContext, lockedModuleId, getQuizzesByModule, getAllQuizzes]);

    const filteredQuizzes = useMemo(() => {
        return (quizzes || []).filter((quiz) => {
            const text = searchTerm.toLowerCase();
            return (
                (quiz.titre || '').toLowerCase().includes(text) ||
                (quiz.description || '').toLowerCase().includes(text) ||
                (quiz.moduleName || '').toLowerCase().includes(text) ||
                (quiz.typeQuiz || '').toLowerCase().includes(text)
            );
        });
    }, [quizzes, searchTerm]);

    const paginatedQuizzes = useMemo(() => {
        return filteredQuizzes.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [filteredQuizzes, currentPage]);

    useEffect(() => {
        const total = Math.ceil(filteredQuizzes.length / itemsPerPage) || 1;
        if (currentPage > total) {
            setCurrentPage(total);
        }
    }, [filteredQuizzes.length, currentPage]);

    const handleOpenCreateModal = () => {
        setEditingQuiz(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (quiz) => {
        setEditingQuiz(quiz);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingQuiz(null);
        setIsModalOpen(false);
    };

    const handleSubmit = (formData) => {
        const payload = lockedModuleId ? { ...formData, moduleId: Number(lockedModuleId) } : formData;
        if (editingQuiz) {
            updateQuiz(editingQuiz.id, payload);
        } else {
            createQuiz(payload);
        }
        handleCloseModal();
    };

    const handleDelete = (quiz) => {
        Swal.fire({
            title: t('Supprimer ?'),
            text: t(`Voulez-vous vraiment supprimer le quiz "${quiz.titre}" ?`),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ee645f',
            cancelButtonColor: '#204385',
            confirmButtonText: t('Oui, supprimer'),
            cancelButtonText: t('Annuler')
        }).then((result) => {
            if (result.isConfirmed) {
                deleteQuiz(quiz.id);
            }
        });
    };

    const handleFilter = () => {
        setCurrentPage(1);
        if (lockedModuleId) {
            getQuizzesByModule(lockedModuleId);
            return;
        }
        if (selectedModuleId) {
            getQuizzesByModule(selectedModuleId);
            return;
        }
        if (selectedType) {
            getQuizzesByType(selectedType);
            return;
        }
        getAllQuizzes();
    };

    const getBadgeClass = (type) => {
        switch (type) {
            case 'STANDARD': return 'qz-badge--blue';
            case 'EVALUATION': return 'qz-badge--purple';
            case 'INTEGRATION': return 'qz-badge--green';
            case 'MUST_LEARN': return 'qz-badge--orange';
            default: return 'qz-badge--grey';
        }
    };

    const renderPagination = ({ totalItems, currentPage, setCurrentPage, label }) => {
        const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

        if (!totalItems || totalItems <= itemsPerPage) return null;

        return (
            <div className="cicada-pagination">
                <div className="cicada-pagination-info">
                    {t('Affichage de')} {(currentPage - 1) * itemsPerPage + 1}{' '}
                    {t('à')} {Math.min(currentPage * itemsPerPage, totalItems)}{' '}
                    {t('sur')} {totalItems} {label}
                </div>

                <div className="cicada-pagination-actions">
                    <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    >
                        {t('Précédent')}
                    </button>

                    <span>
                        {t('Page')} {currentPage} / {totalPages}
                    </span>

                    <button
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    >
                        {t('Suivant')}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="qz-page">
            <div className="qz-header">
                <div>
                    <h2>{t('Gestion des Quiz')}</h2>
                    {effectiveModuleContext && (
                        <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
                            {t('Module')} : {MODULE_CONTEXT_LABELS[effectiveModuleContext] || effectiveModuleContext}
                        </p>
                    )}
                    {/*<p>{t('Gérez vos quiz, questions et évaluations de formation')}</p>*/}
                </div>
                <button className="qz-btn qz-btn-primary" onClick={handleOpenCreateModal}>
                    <Plus size={15} /> {t('Ajouter un quiz')}
                </button>
            </div>

            <div className="qz-toolbar">
                <div className="qz-search-wrap">
                    <Search size={14} className="qz-search-icon" />
                    <input
                        type="text"
                        className="qz-search"
                        placeholder={t('Rechercher un quiz...')}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="qz-filter-group">
                    <select
                        className="qz-select"
                        value={selectedType}
                        onChange={(e) => {
                            setSelectedType(e.target.value);
                            if (e.target.value) setSelectedModuleId('');
                            setCurrentPage(1);
                        }}
                    >
                        <option value="">{t('Type')}</option>
                        {QUIZ_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    <select
                        className="qz-select"
                        value={selectedModuleId}
                        onChange={(e) => {
                            setSelectedModuleId(e.target.value);
                            if (e.target.value) setSelectedType('');
                            setCurrentPage(1);
                        }}
                        disabled={!!lockedModuleId}
                    >
                        <option value="">{t('Module')}</option>
                        {contextModules.map((module) => (
                            <option key={module.id} value={module.id}>{getModuleLabel(module)}</option>
                        ))}
                    </select>

                    <button className="qz-btn qz-btn-primary" onClick={handleFilter} title={t('Filtrer')}>
                        <Search size={14} />
                    </button>

                    <button className="qz-btn qz-btn-light" onClick={() => {
                        setSelectedModuleId('');
                        setSelectedType('');
                        if (lockedModuleId) {
                            setSelectedModuleId(String(lockedModuleId));
                            getQuizzesByModule(lockedModuleId);
                        } else {
                            getAllQuizzes();
                        }
                        setCurrentPage(1);
                    }}>
                        <List size={14} /> {t('Tous')}
                    </button>
                </div>

                <div style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color: "#6b7280" }}>
                    {loading ? "..." : `${filteredQuizzes.length} quiz`}
                </div>
            </div>

            <div className="qz-card">
                <div className="qz-table-wrap">
                    <table className="qz-table">
                        <thead>
                            <tr>
                                <th>{t('Titre')}</th>
                                <th>{t('Module')}</th>
                                <th>{t('Type')}</th>
                                <th>{t('Score mini')}</th>
                                <th>{t('Questions')}</th>
                                {/*<th>{t('Status')}</th>*/}
                                <th style={{ textAlign: "right" }}>{t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: "center", padding: 32 }}>{t('Chargement...')}</td>
                                </tr>
                            ) : filteredQuizzes.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: "center", padding: 32, opacity: 0.6 }}>{t('Aucun quiz trouvé')}</td>
                                </tr>
                            ) : (
                                paginatedQuizzes.map((quiz) => (
                                    <tr key={quiz.id}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <HelpCircle size={14} style={{ color: "#ee645f" }} />
                                                <div>
                                                    <div style={{ fontWeight: 700, color: "#204385" }}>{quiz.titre}</div>
                                                    {quiz.description && <div style={{ fontSize: 10, opacity: 0.7 }}>{quiz.description}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="qz-badge qz-badge--blue">
                                                {quiz.moduleName || '-'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`qz-badge ${getBadgeClass(quiz.typeQuiz)}`}>
                                                {quiz.typeQuiz}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 600 }}>{quiz.seuilReussite}%</span>
                                        </td>
                                        <td>
                                            <span className="qz-badge qz-badge--grey">
                                                {quiz.nombreQuestions ?? 0}
                                            </span>
                                        </td>
                                        {/*<td>*/}
                                        {/*    {quiz.estObligatoire ? (*/}
                                        {/*        <span className="qz-badge qz-badge--red">{t('Obligatoire')}</span>*/}
                                        {/*    ) : (*/}
                                        {/*        <span className="qz-badge qz-badge--grey">{t('Optionnel')}</span>*/}
                                        {/*    )}*/}
                                        {/*</td>*/}
                                        <td style={{ textAlign: "right" }}>
                                            <div className="qz-actions">
                                                <button
                                                    className="qz-icon-btn"
                                                    title={t('Gérer les questions')}
                                                    onClick={() => navigate(`${questionsBasePath}/${quiz.id}/questions`, { state: { quiz, quizBasePath: questionsBasePath } })}
                                                >
                                                    <HelpCircle size={15} />
                                                </button>

                                                {/*<button*/}
                                                {/*    className="quiz-btn quiz-btn-primary"*/}
                                                {/*    onClick={() => navigate(`/quiz-player/${quiz.id}?collaborateurId=${currentUser.id}&refContexteId=${quiz.refContexteId}`)}*/}
                                                {/*>*/}
                                                {/*    ▶️ Passer le quiz*/}
                                                {/*</button>  <button*/}
                                                {/*    className="quiz-btn quiz-btn-primary"*/}
                                                {/*    onClick={() => navigate(`/quiz-history/${currentUser.id}`)}*/}
                                                {/*>*/}
                                                {/*    ▶️ history le quiz*/}
                                                {/*</button>*/}
                                                <button
                                                    className="qz-icon-btn"
                                                    title={t('Modifier')}
                                                    onClick={() => handleOpenEditModal(quiz)}
                                                >
                                                    <Edit size={15} />
                                                </button>
                                                <button
                                                    className="qz-icon-btn qz-icon-btn--danger"
                                                    title={t('Supprimer')}
                                                    onClick={() => handleDelete(quiz)}
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {renderPagination({
                    totalItems: filteredQuizzes.length,
                    currentPage,
                    setCurrentPage,
                    label: t('quiz')
                })}
            </div>

            <QuizModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                editingQuiz={editingQuiz}
                loading={loading}
                modules={contextModules}
                skills={skills}
                domaines={domaines}
                fetchSkillsByQuizMode={fetchSkillsByQuizMode}
                lockedModuleId={lockedModuleId}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    quizzes: state.quizzes?.quizzes || [],
    loading: state.quizzes?.loading,
    message: state.quizzes?.message,
    error: state.quizzes?.error,
    modules: state.module?.modules || [],
    skills: state.skills?.list || [],
    domaines: state.paramDomainesThemes?.domaines || []
});

const mapDispatchToProps = (dispatch) => ({
    getAllQuizzes: () => dispatch(getAllQuizzesRequest()),
    getQuizzesByModule: (moduleId) => dispatch(getQuizzesByModuleRequest(moduleId)),
    getQuizzesByType: (typeQuiz) => dispatch(getQuizzesByTypeRequest(typeQuiz)),
    createQuiz: (quizData) => dispatch(createQuizRequest(quizData)),
    updateQuiz: (quizId, quizData) => dispatch(updateQuizRequest(quizId, quizData)),
    deleteQuiz: (quizId) => dispatch(deleteQuizRequest(quizId)),
    getAllModules: () => dispatch(getAllModulesRequest()),
    getAllDomaines: () => dispatch(fetchDomainesRequest()),
    fetchSkillsByQuizMode: (domaineId) => dispatch(fetchSkillsByQuizModeRequest(domaineId)),
    clearMessage: () => dispatch(clearQuizMessage())
});

export default connect(mapStateToProps, mapDispatchToProps)(QuizManagement);
